import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface ColumnSeries { key: string; label: string; className: string; values: number[] }

/** Axis top: 1, 2 or 5 times a power of ten, at least the data max. */
export function niceMax(n: number): number {
  if (n <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(n)));
  const m = n / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

/**
 * Weekly stacked columns. The first series sits at the bottom; stacked parts
 * keep a 2px gap; every column has a hover tooltip with each series' value.
 */
export function StackedColumns({ labels, series, labelEvery = 2, notes = [], formatValue = (n: number) => String(n) }: {
  labels: string[];
  series: ColumnSeries[];
  labelEvery?: number;
  /** Tooltip-only lines, not drawn: "of which meals" under meetings. */
  notes?: { label: string; values: number[] }[];
  formatValue?: (n: number) => string;
}) {
  const totals = labels.map((_, i) => series.reduce((s, x) => s + (x.values[i] ?? 0), 0));
  const max = niceMax(Math.max(0, ...totals));
  const ticks = [...new Set([0, Math.round(max / 2), max])];
  const cols = `2rem repeat(${labels.length}, minmax(0, 1fr))`;
  const summary = `${labels.length} weeks: ${labels.map((l, i) => `${l} ${totals[i]}`).join(", ")}`;
  return (
    <div role="group" aria-label={summary} className="h-full min-h-0 flex flex-col px-2 pt-2 pb-1 text-[10px] text-muted-foreground">
      <div className="flex-1 min-h-0 grid" style={{ gridTemplateColumns: cols }}>
        <div className="relative">
          {ticks.map((t) => (
            <span key={t} className="absolute right-1.5 -translate-y-1/2 tabular-nums" style={{ top: `${(1 - t / max) * 100}%` }}>{formatValue(t)}</span>
          ))}
        </div>
        {labels.map((label, i) => {
          const colLabel = `${label}: ${[...series, ...notes].map((s) => `${s.label} ${formatValue(s.values[i] ?? 0)}`).join(", ")} (total ${formatValue(totals[i])})`;
          const parts = [...series].reverse().filter((s) => (s.values[i] ?? 0) > 0);
          return (
            <Tooltip key={label}>
              <TooltipTrigger asChild>
                <div tabIndex={0} role="img" aria-label={colLabel} className="relative h-full flex flex-col justify-end items-center border-b border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {parts.map((s, idx) => (
                    <span
                      key={s.key}
                      className={cn("w-3/5", idx === 0 && "rounded-t-sm", s.className)}
                      style={{ height: `${((s.values[i] ?? 0) / max) * 100}%`, marginTop: idx ? 2 : 0 }}
                    />
                  ))}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Week of {label}</p>
                {series.map((s) => <p key={s.key}>{s.label}: {formatValue(s.values[i] ?? 0)}</p>)}
                {notes.map((n) => <p key={n.label} className="text-muted-foreground">{`${n.label}: ${formatValue(n.values[i] ?? 0)}`}</p>)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      <div className="grid pt-1" style={{ gridTemplateColumns: cols }}>
        <span />
        {labels.map((l, i) => <span key={l} className="text-center truncate">{i % labelEvery === 0 ? l : ""}</span>)}
      </div>
    </div>
  );
}
